import { Avatar } from 'antd';
import IconClockIn from 'assets/Icons/clockIn';
import Box from 'components/Box';
import Button from 'components/Button';
import Text from 'components/Text';
import { IStaffAttendanceItem } from 'features/user/services/types/clock-in-out';
import styled from 'styled-components';
import Time from 'assets/Icons/time';
import ClockOut from 'assets/Icons/clockOut';
import { getStaffPositionLabel, STAFF_POSITION_INFO } from 'features/settingStaff/services/constants';

interface StaffAttendanceItemProps {
  item: IStaffAttendanceItem;
  onPress: (isClockIn: boolean, pinCode: string) => void;
}

const StaffAttendanceItem = ({ item, onPress }: StaffAttendanceItemProps) => {
  return (
    <Container>
      <AvatarContainer>
        <Avatar src={item?.avatar} size={80}>
          {item?.staffName?.[0]?.toUpperCase()}
        </Avatar>
        <div className={`clock-status ${item?.clockIn ? 'clocked-in' : 'clocked-out'}`} />
      </AvatarContainer>
      <Content>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Text variant='H5' textTransform='uppercase'>
            {item?.staffName}
          </Text>
          <Text variant='CONTENT_2' color='text_3_v2' style={{ fontWeight: 500 }}>
            {getStaffPositionLabel(item?.position as STAFF_POSITION_INFO)}
          </Text>
        </Box>
        <Box className={`clock-status ${item?.clockIn ? 'clocked-in' : 'clocked-out'}`}>
          <Text variant='BODY_2' color={item?.clockIn ? 'v2_green_6' : 'text_4_v2'}>
            {item?.clockIn ? 'Clocked In' : 'Clocked Out'}
          </Text>
        </Box>

        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box display='flex' alignItems='center' gap='1' justifyContent='center'>
            {item?.clockIn ? (
              <>
                <IconClockIn fill='#64748B'/>
                <Text variant='CONTENT_3' color='text_3_v2' style={{ lineHeight: 1 }}>
                  {item?.clockInTime}
                </Text>
              </>
            ) : (
              <>
                <Time fill='#64748B' />
                <Text variant='CONTENT_3' color='text_3_v2' style={{ lineHeight: 1 }}>
                  Not Clock In
                </Text>
              </>
            )}
          </Box>
          {item?.clockIn ? (
            <Button
              ntype='GHOST_PRIMARY'
              iconElement={<ClockOut width={24} height={24} fill='#D2464F' />}
              size='small'
              onClick={() => onPress(true, item?.pinCode)}
            >
              Clock Out
            </Button>
          ) : (
            <Button
              ntype='GREEN'
              color='v2_green_6'
              iconElement={<IconClockIn fill='#FFF' />}
              size='small'
              onClick={() => onPress(false, item?.pinCode)}
            >
              Clock In
            </Button>
          )}
        </Box>
      </Content>
    </Container>
  );
};

export default StaffAttendanceItem;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  background: #fff;
  font-family: 'Poppins';

  box-shadow:
    0 2px 4px 0 rgba(44, 35, 66, 0.4),
    0 4px 8px -3px rgba(45, 35, 66, 0.3),
    0 -3px 0 0 #d6d6e7 inset;

  &:hover {
    box-shadow:
      0 4px 8px 0 rgba(44, 35, 66, 0.4),
      0 8px 16px -3px rgba(45, 35, 66, 0.3),
      0 -3px 0 0 #d6d6e7 inset;
  }
`;

const AvatarContainer = styled.div`
  position: relative;

  .clock-status {
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    bottom: 0;
    right: 0;
    border: 2px solid #fff;

    &.clocked-in {
      background-color: #0da472;
    }

    &.clocked-out {
      background-color: #adadad;
    }
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .clock-status {
    width: fit-content;
    display: flex;
    align-items: center;
    padding: 2px 4px;
    border-radius: 4px;

    &.clocked-in {
      background-color: #effffa;
    }

    &.clocked-out {
      background-color: #e5e6eb;
    }
  }
`;
